import { Compass } from 'lucide-react';
import { appPath, workAreaRoutePath } from '@featit/shared/addressing/paths';
import {
  workAreaQualifiedLabel,
  workAreaRouteRef,
} from '@featit/shared/addressing/workAreaRouteRef';
import { type IProject } from '@featit/shared/types/index';
import { useT } from '@/i18n/LocaleProvider';
import { AddressNotFound } from './AddressNotFound';
import { quotableAddressPart } from './quotableAddressPart';

/**
 * What an address that names nothing shows (I-0449): that nothing lives here, and the way back.
 *
 * On a work area that exists (`/acme/tracker/-/nope`), it names that work area and leads back to its
 * items, never to "no work area": the work area is there, only the page is not (F-0250).
 *
 * ⛔ Never a guess at what the reader meant, and never another entity's page: a request for an
 * address nobody declared is answered before anything is fetched.
 */
export function NoPageHere({ address, workArea }: { address: string; workArea?: IProject }) {
  const t = useT();
  const label = workArea ? workAreaQualifiedLabel(workArea) : null;
  const quoted = quotableAddressPart(address);
  const where = quoted ? t('notFound.atAddress', { address: quoted }) : t('notFound.atThisAddress');
  return (
    <AddressNotFound
      title={t('notFound.title')}
      description={
        workArea
          ? t('notFound.inWorkArea', { workArea: label, where })
          : t('notFound.nowhere', { where })
      }
      next={{
        to: workArea ? workAreaRoutePath(workAreaRouteRef(workArea), '/items') : appPath('/items'),
        label: workArea
          ? t('notFound.goToWorkAreaItems', { workArea: workArea.name })
          : t('notFound.goToItems'),
        icon: <Compass className="h-4 w-4 text-gray-400" aria-hidden="true" />,
      }}
    />
  );
}
